import type { CSSProperties } from '@linaria/core';
import { cx } from '@linaria/core';
import { SyntheticEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { openMenuDialog } from '../../../../actions/menu';
import { goToNotes } from '../../../../actions/nav';
import { addFriend, bookmarkModel, removeBookmark, removeFriend } from '../../../../actions/user';
import { AppStateType, SecureStateType, SettingsType, VideoType } from '../../../../constants/enums';
import locales from '../../../../locales/en';
import { GridDensityUeopt } from '../../../../mfcLogic/ueopts';
import { isLevelGuest } from '../../../../mfcLogic/userProperties/level';
import { TransitionTypes } from '../../../../state';
import { clearAvatarInfoOpenID, clearAvatarInfoOpenTimer, isMouseMovementHighState, onHoverMomentumSettled, resetAvatarInfoOpenIDState, setAvatarInfoOpenID } from '../../../../state/state.grid';
import { openRoom, pinRoom, setLoungeSurrogate } from '../../../../state/state.rooms';
import { isTouchDevice } from '../../../../utilities/browser';
import { motionTransition } from '../../../../utilities/transitions';
import ButtonModelMenu from '../../../Buttons/ButtonModelMenu';
import { Fade, Grow } from '../../../common/transitions';
import { useAppState } from '../../../Hooks/useAppState';
import { useDragHelper } from '../../../Hooks/useDragHelper';
import { useGridAvatarInfoOpenAppState } from '../../../Hooks/useGridAvatarInfoOpenAppState';
import { useGridPreviewOnScrollTileState, useGridScrollCursorTileState } from '../../../Hooks/useGridPreviewOnScrollAppState';
import { useFloatingPreviewSelector } from '../../../Hooks/useModal';
import { useIsRoomPinned } from '../../../Hooks/useRoom';
import { useSecureState } from '../../../Hooks/useSecureState';
import { useSettingSelector } from '../../../Hooks/useSettings';
import { useUserSelector } from '../../../Hooks/useUser';
import CameraIcon from '../../../Icons/Camera';
import NotesIcon from '../../../Icons/Notes';
import PinIcon from '../../../Icons/Pin';
import RoomIcon from '../../../Icons/Room';
import IconViewers from '../../../Icons/Viewers';
import { palette } from '../../../styles/colors';
import layers from '../../../styles/layers';
import shape from '../../../styles/shape';
import CircularProgress from '../../CircularProgress';
import LiveSnapStream from '../../LiveSnapStream';
import BookmarkBadge from '../Badges/Bookmark';
import FriendBadge from '../Badges/Friend';
import MissMFCBadge from '../Badges/MissMFC';
import NewBadge from '../Badges/New';
import OnlineBadge from '../Badges/Online';
import PopularBadge from '../Badges/Popular';
import PriorMissMFCBadge from '../Badges/PriorMissMFC';
import RecommendationBadge from '../Badges/Recommendation';
import StatusBadge from '../Badges/Status';
import TrendingBadge from '../Badges/Trending';
import ClubShowBanner from '../Banners/ClubShow';
import GroupShowBanner from '../Banners/GroupShow';
import MissMFCBanner from '../Banners/MissMFC';
import NewModelBanner from '../Banners/NewModel';
import PopularBanner from '../Banners/Popular';
import PrivateShowBanner from '../Banners/PrivateShow';
import TrendingBanner from '../Banners/Trending';
import BookmarkButton from '../Buttons/BookmarkButton';
import CloseButton from '../Buttons/CloseButton';
import FriendButton from '../Buttons/FriendButton';
import InfoButton from '../Buttons/InfoButton';
import NotesButton from '../Buttons/NotesButton';
import PinButton from '../Buttons/PinButton';
import RoomButton from '../Buttons/RoomButton';
import CellImage from '../CellImage';
import Container from '../Container';
import { FADE_TRANSITION_AVATAR, GetStylesConfig, TIMEOUT_ANIMATION_AVATAR, getAnonymousMissMFCRank, isAnonymousMissMFC, resolveDisplayHighlight, resolveModelStatus } from '../helpers';
import LargeFloatingPreview from '../LargeFloatingPreview';
import ModelName from '../ModelName';
import { VideoCover } from '../VideoCover';
import "./Avatar.linaria.module.css!=!./Avatar?./Avatar.linaria.module.css?css=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"
const PREVIEW_ON_SCROLL_DELAY = 0;
// how long a hover preview may take before the spinner appears, so a
// preview that loads promptly never flashes one

const headerUtilityRootStyles = "atm_fc_1yb4nlp atm_9s_1txwivl atm_vy_1osqo2v";
interface HeaderUtilityProps {
  showLargePreviewButton?: boolean;
  showInfoCloseButton?: boolean;
  showBookmarkButton?: boolean;
  showFriendButton?: boolean;
  showNotesButton?: boolean;
  showRoomButton?: boolean;
  showPinButton?: boolean;
  isVideoOverlay?: boolean;
  isRoomPinned?: boolean;
  onCloseInfo?: (event?: any) => void;
  onBookmark?: (event?: any) => void;
  onFriend?: (event?: any) => void;
  onRoom?: (event?: any) => void;
  onPinRoom?: (event?: any) => void;
  isBookmark?: boolean;
  isFriend?: boolean;
  isMember?: boolean;
  onOpenNote?: (event?: any) => void;
  iconSize?: number;
  id: number;
}
const HeaderUtility = memo((props: HeaderUtilityProps) => {
  const {
    showLargePreviewButton,
    showInfoCloseButton,
    showBookmarkButton,
    showFriendButton,
    showNotesButton,
    showRoomButton,
    showPinButton,
    isVideoOverlay,
    isRoomPinned,
    onCloseInfo,
    onBookmark,
    isBookmark,
    onOpenNote,
    onPinRoom,
    onFriend,
    isFriend,
    iconSize,
    onRoom,
    isMember,
    id
  } = props;
  const {
    ageGateActive
  } = useSecureState(SecureStateType.AGE_VERIFICATION);
  const level = useUserSelector('me', 'level');
  const isGuest = isLevelGuest(level);
  return <div data-testid="Container__HeaderUtility" className={headerUtilityRootStyles}>
            {showLargePreviewButton && <LargeFloatingPreview color={palette.backgroundReverse} size={iconSize} shadow={false} id={id} />}
            {showBookmarkButton && <BookmarkButton color={palette.backgroundReverse} size={iconSize} filled={isBookmark}
    // style={{
    //     border: `1px solid ${resolveButtonHighlight({
    //         isBookmark,
    //     })}`,
    // }}
    title={isBookmark ? 'Unbookmark' : 'Bookmark'} onClick={onBookmark} shadow={false} disabled={isGuest && ageGateActive} />}
            {showInfoCloseButton && <CloseButton size={iconSize} onClick={onCloseInfo} />}
            {showFriendButton && !showPinButton && !showInfoCloseButton && !showBookmarkButton && <FriendButton color={palette.backgroundReverse} size={iconSize} filled={isFriend} title={isFriend ? 'Unfriend' : 'Add Friend'} onClick={onFriend} shadow={false} disabled={isGuest && ageGateActive} />}
            {showRoomButton && !isMember && !showNotesButton && <RoomButton color={palette.backgroundReverse} size={iconSize} filled title="Open Room" onClick={onRoom} shadow={false} />}
            {showNotesButton && <NotesButton color={palette.backgroundReverse} size={iconSize} filled title="Open Notes" onClick={onOpenNote} shadow={false} />}
            {showFriendButton && (showPinButton || showInfoCloseButton || showBookmarkButton) && <FriendButton color={palette.backgroundReverse} size={iconSize} filled={isFriend} title={isFriend ? 'Unfriend' : 'Add Friend'} onClick={onFriend} shadow={false} disabled={isGuest && ageGateActive} />}
            {showPinButton && <PinButton title={(isRoomPinned ? 'Unpin' : 'Pin') + ' Model'} color={isVideoOverlay ? palette.commonWhite : palette.backgroundReverse} size={iconSize} filled={isRoomPinned} shadow={isVideoOverlay} onClick={onPinRoom} />}
        </div>;
});
const resolveNewBadgePosition = options => {
  const {
    stylesBadgeStatus
  } = options;
  const padding = {
    '--padding-bottom': stylesBadgeStatus('offsetY') - 1 + 'px',
    '--padding-right': stylesBadgeStatus('offsetX') - 1 + 'px',
    '--padding-left': stylesBadgeStatus('offsetX') - 1 + 'px',
    '--padding-top': stylesBadgeStatus('offsetY') - 1 + 'px'
  };

  // // Simply to get around TS issues:
  // // https://github.com/cssinjs/jss/issues/1344
  // const column: React.CSSProperties  = {
  //     flexDirection: 'column',
  // }
  const columnReverse: CSSProperties = {
    '--flex-direction': 'column-reverse'
  };

  // if (
  //     !isRoomPinned &&
  //     !canDisplayNotesButton &&
  //     !canDisplayRecentlyVisitedBadge
  // ) {
  //     return {
  //         ...padding,
  //         ...column,
  //         alignItems: 'flex-end',
  //         right: 0,
  //         top: 0,
  //     }
  // }

  // if (!canDisplayInfoButton && !isLargePreviewPinned) {
  //     return {
  //         ...padding,
  //         ...column,
  //         alignItems: 'flex-start',
  //         left: 0,
  //         top: 0,
  //     }
  // }

  return {
    ...padding,
    ...columnReverse,
    '--bottom': 0 + 'px',
    '--right': 0 + 'px'
  };
};
const borderRadiusBottomLeft = "atm_43_1y44olf atm_6c_1y44olf";
const borderRadiusBottomRight = "atm_45_1y44olf atm_6a_1y44olf";
const borderRadiusTopLeft = "atm_6a_1y44olf atm_45_1y44olf";
export const resolveNewBadgeBorderRadius = options => {
  const {
    canDisplayRecentlyVisitedBadge,
    canDisplayNotesButton,
    // isLargePreviewPinned,
    canDisplayInfoButton,
    isRoomPinned
  } = options;
  if (!isRoomPinned && !canDisplayNotesButton && !canDisplayRecentlyVisitedBadge) {
    return borderRadiusBottomLeft;
  }
  if (!canDisplayInfoButton) {
    // && !isLargePreviewPinned)
    return borderRadiusBottomRight;
  }
  return borderRadiusTopLeft;
};
const rootContainer = "atm_ar_1bp4okc atm_9s_1txwivl";
const avatarContainer = "atm_e2_1dk5qia atm_mk_h2mmj6 atm_bb_idpfg4";
const headerRootContainerStyles = "atm_e2_1dk5qia atm_h_1h6ojuz atm_9s_1txwivl";
const stylesHeaderContainer = "atm_e2_1dk5qia atm_vy_rzutr2 atm_h_1h6ojuz atm_vl_15vqwwr atm_mk_stnw88 atm_9s_1txwivl atm_wq_18h8ryc atm_tk_idpfg4";
const stylesHeaderContainerVisibility = "atm_vl_ewfl5b";
const stylesPreviewFrame = "atm_iy_8wry8b atm_e2_1dk5qia atm_uc_1ie8p39 atm_fb_1cl4t0h atm_ks_15vqwwr atm_5j_i2wt44 atm_70_1m02n3y atm_mk_h2mmj6 atm_vy_1osqo2v";
const stylesPreviewFrameBorder = "atm_3f_1fmrp5d";
// the scroll-cursor ring is a pseudo-element stacked above the tile's
// content rather than an outline on the frame: Chrome and WebKit paint a
// stacking context's own outline before its positioned descendants, so an
// outline is hidden by the poster image until the preview replaces it.
// Longhands so the cursor atom below only has to flip the color.
const stylesPreviewFrameScrollCursorRing = "atm_7otamn_i2wt44 atm_rxbg8t_1jer2an atm_1rpvkn0_1j28jx2 atm_10exmsa_glywfm atm_1yyqffb_nqa18y atm_2zt8x3_stnw88 atm_1g5r1jr_yh40bf atm_1938jqx_1yyfdc7 atm_grwvqw_cs5v99 atm_7haktb_idpfg4";
// lights up fast and (via the ring's transition) fades out slowly, so the
// tile the cursor just left trails the one it landed on
const stylesPreviewFrameScrollCursor = "atm_rxbg8t_u0dxgg atm_1rpvkn0_13p744v";
const newMfcBadgeContainerStyles = "atm_ar_4clnmf atm_le_1evqb17 atm_ll_1c54f9w atm_lk_m17xx7 atm_lo_1shzrec atm_c8_137efj2 atm_6i_1t3ix0y atm_n3_gev6tx atm_h_esu3gu atm_mk_stnw88 atm_ks_15vqwwr atm_9s_1txwivl atm_wq_kb7nvz";
const statusContainer = "atm_ar_1bp4okc atm_mk_stnw88 atm_h_1h6ojuz atm_9s_1txwivl atm_n3_idpfg4 atm_tk_yh40bf";
const infoContainer = "atm_ar_1bp4okc atm_fc_1y6m0gg atm_h_1h6ojuz atm_mk_stnw88 atm_ks_15vqwwr atm_9s_1txwivl atm_wq_kb7nvz atm_fq_idpfg4 atm_tk_idpfg4";
const stylesViewerCounter = "atm_c8_137efj2 atm_gz_yh40bf";
const badgeRootContainerStyles = "atm_mk_stnw88 atm_9s_1txwivl atm_6i_idpfg4 atm_fq_idpfg4";
const badgeContainerStyles = "atm_gq_2efq5t atm_gz_1amm6g8 atm_uc_1mzj4p5 atm_ar_1bp4okc atm_9s_1txwivl";
const missMfcBadgeStyles = "atm_70_1i8pdnf atm_43_1y44olf atm_c8_137efj2 atm_fc_1h6ojuz atm_6c_1y44olf atm_h_1h6ojuz atm_2d_u67f1s atm_cs_8n312b atm_9s_1txwivl atm_vy_1osqo2v atm_7l_1d6myej atm_k4_6xdddb atm_h3_yh40bf atm_lo_idpfg4 atm_le_idpfg4 atm_lk_yh40bf atm_ll_yh40bf";
const modelMenuContainerStyles = "atm_mk_stnw88 atm_6i_idpfg4 atm_n3_idpfg4";
const stylesNewBadge = "atm_70_1i8pdnf atm_2d_u67f1s atm_k4_6xdddb";
const stylesPriorMissMFCBadge1 = "atm_e2_1dk5qia atm_vy_rzutr2";
const stylesPriorMissMFCBadge2 = "atm_e2_1dk5qia atm_vy_rzutr2 atm_gz_yh40bf atm_h3_yh40bf";
const stylesPriorMissMFCBadgeMargin = "atm_gi_1fbqqyq";
const stylesContainer = "atm_k4_6xdddb atm_lo_1y44olf atm_le_1y44olf atm_lk_1y44olf atm_ll_1y44olf";
const stylesContainerAlt = "atm_lo_1y44olf atm_le_1y44olf atm_lk_1y44olf atm_ll_1y44olf";
const stylesCellImage = "atm_mk_h2mmj6";
const stylesHeightAndFontSize = "atm_c8_137efj2 atm_e2_1dk5qia";
const stylesMargin = "atm_gi_181067c";
const stylesNotesButton = "atm_gz_j39m9b atm_h3_j39m9b";
const stylesLiveSnapStream = "atm_5j_1koqnlb atm_mk_stnw88 atm_ks_15vqwwr atm_c8_19bvopo atm_tk_idpfg4";
const stylesLiveSnapStreamBadgeBottom = "atm_8gve3a_1wugsn5 atm_s577uo_u7u8ut";
const stylesLiveSnapStreamScale = "atm_106bygc_w4h6rm atm_nf_17jhm3v atm_oyp9lw_ssltny";
const stylesPreviewLoading = "atm_fc_1h6ojuz atm_mj_glywfm atm_h_1h6ojuz atm_mk_stnw88 atm_9s_1txwivl atm_f3_idpfg4 atm_p_1rlwmvk";
// the spinner sits on an arbitrary avatar photo, so it gets its own dark
// disc to stay legible in either theme
const stylesPreviewLoadingDisc = "atm_folmkk_1638ien atm_2d_boidks atm_5j_1ssbidh atm_9s_1txwivl atm_lo_i2wt44 atm_le_i2wt44 atm_lk_i2wt44 atm_ll_i2wt44";
const stylesStatusBadge = "atm_uc_squqqo atm_c8_137efj2 atm_ks_15vqwwr atm_iy_12xxubj atm_j3_12xxubj atm_lo_idpfg4 atm_le_idpfg4 atm_lk_yh40bf atm_ll_yh40bf atm_h3_yh40bf";
const stylesStatusBadgeRoomPreview = "atm_iy_grho7r atm_j3_14noui3";
const stylesDisplayBadges = "atm_fc_1tte0sa atm_9s_1txwivl";
const stylesDisplayMissMFCBadge = "atm_9s_1txwivl";
const stylesModelMenuButtonContainer = "atm_gq_2efq5t atm_gz_1wugsn5";
const stylesModelName = "atm_45_i2wt44 atm_43_idpfg4 atm_fc_1y6m0gg atm_c8_137efj2 atm_e2_1dk5qia atm_h_1h6ojuz atm_uc_drx15w atm_h3_wqw3k9";
const stylesModelNameBorderTop = "atm_67_bke03k";
const stylesModelNameCenter = "atm_fc_1h6ojuz";
const stylesInfoButton = "atm_gz_j39m9b atm_h3_j39m9b atm_k4_dzwcw0";
const stylesInfoButtonVisibility = "atm_vl_15vqwwr";
const stylesOnlineBadge = "atm_h3_yh40bf atm_k4_6xdddb";
const stylesMissMFCGold = "atm_7l_rnytqd";
const stylesMissMFCSilver = "atm_7l_1g81rod";
const stylesViewerContainer = "atm_aj_10fvni5 atm_gq_2efq5t atm_h_1h6ojuz atm_h0_1y44olf atm_cs_8n312b atm_h3_yh40bf atm_9s_1txwivl atm_7l_u67f1s";
const stylesPreviewFrame43ViewerContainer = "atm_gq_exct8b";
const stylesPreviewFrame43MenuContainer = "atm_gq_yh40bf atm_h0_yh40bf";
interface AvatarProps {
  activeRoom?: number;
  columnCount?: number;
  columnIndex?: number;
  columnWidth: number;
  disableMenuNextPrev?: boolean;
  disableInteractions?: boolean;
  displayBookmarkBadge?: boolean;
  displayBookmarkButton?: boolean;
  displayBookmarkHighlight?: boolean;
  displayFriendBadge?: boolean;
  displayFriendButton?: boolean;
  displayFriendHighlight?: boolean;
  displayGroupShowBanner?: boolean;
  displayPrivateShowBanner?: boolean;
  displayClubShowBanner?: boolean;
  displayHeight: number;
  displayMissMFCBadge?: boolean;
  displayMissMFCBanner?: boolean;
  displayNewModelBadge?: boolean;
  displayNewModelBanner?: boolean;
  displayNoteIconButton?: boolean;
  displayOnlineBadge?: boolean;
  displayPopularBadge?: boolean;
  displayPopularBanner?: boolean;
  displayPopularHighlight?: boolean;
  displayRecentlyVisitedBadge?: boolean;
  displayRecentlyVisitedHighlight?: boolean;
  displayRecommendationBadge?: boolean;
  displayRecommendationHighlight?: boolean;
  displayStatusBadge?: boolean;
  displayTrendingBadge?: boolean;
  displayTrendingBanner?: boolean;
  displayTrendingHighlight?: boolean;
  enableLargePreview?: boolean;
  previewScrollEnabled?: boolean;
  enablePin?: boolean;
  enablePinToTopSymbols?: boolean;
  frameShadow?: string;
  getStyle: (key: string, config?: GetStylesConfig) => any;
  gridPadding: number;
  gridDensity?: GridDensityUeopt;
  id: number;
  index?: number;
  loungeSlug?: string;
  missMFCPrizeOverride?: number;
  missMFCRankOverride?: number;
  name: string;
  onBookmark?: (id: number, isBookmark: boolean) => boolean;
  onFriend?: (id: number, isFriend: boolean) => boolean;
  onOpen?: (event: SyntheticEvent) => void;
  pinLimitReached?: boolean;
  singlePin?: boolean;
  slug: string;
}
const Avatar = memo((props: AvatarProps) => {
  const {
    displayRecentlyVisitedHighlight,
    displayRecommendationHighlight,
    displayRecentlyVisitedBadge,
    displayRecommendationBadge,
    displayPrivateShowBanner,
    displayTrendingHighlight,
    displayBookmarkHighlight,
    displayPopularHighlight,
    displayFriendHighlight,
    displayGroupShowBanner,
    displayClubShowBanner,
    displayTrendingBanner,
    displayNewModelBanner,
    displayNoteIconButton,
    displayPopularBanner,
    displayMissMFCBanner,
    displayBookmarkBadge,
    displayTrendingBadge,
    displayNewModelBadge,
    displayPopularBadge,
    displayMissMFCBadge,
    displayOnlineBadge,
    displayStatusBadge,
    displayFriendBadge,
    enablePinToTopSymbols,
    missMFCPrizeOverride,
    missMFCRankOverride,
    disableMenuNextPrev,
    disableInteractions,
    enableLargePreview,
    previewScrollEnabled,
    pinLimitReached,
    displayHeight,
    frameShadow,
    columnCount,
    columnWidth,
    columnIndex,
    gridPadding,
    gridDensity,
    activeRoom,
    loungeSlug,
    singlePin,
    enablePin,
    index,
    slug,
    name,
    id,
    onBookmark = () => {},
    onFriend = () => {},
    getStyle,
    onOpen
  } = props;
  const {
    isMobile
  } = useAppState(AppStateType.APP);
  const isSmallMobileTiles = (gridDensity === GridDensityUeopt.small || gridDensity === GridDensityUeopt.medium) && isMobile;
  const willShowPreviewOnScroll = useGridPreviewOnScrollTileState(index, columnIndex, !!previewScrollEnabled);
  // outline that sweeps the tiles left to right while scrolling, whether
  // or not the scroll is slow enough for a preview to show
  const isScrollCursorTile = useGridScrollCursorTileState(index, columnIndex, !!previewScrollEnabled);
  const level = useUserSelector('me', 'level');
  const isGuest = isLevelGuest(level);
  const snapRef = useRef<HTMLImageElement>(undefined);
  const {
    dragStart,
    dragMove,
    dragEnd
  } = useDragHelper(id, snapRef);
  const [sort] = useSettingSelector(SettingsType.DEFAULT, 'sort');
  const isPinnedToTopRecentlyVisited = useUserSelector(id, 'isPinnedToTopRecentlyVisited');
  const isPinnedToTopTrending = useUserSelector(id, 'isPinnedToTopTrending');
  const isPinnedToTopPopular = useUserSelector(id, 'isPinnedToTopPopular');
  const isPinnedToTopMissMFC = useUserSelector(id, 'isPinnedToTopMissMFC');
  const isBanned = useUserSelector(id, 'banned');
  const isGroupShowPending = useUserSelector(id, 'isGroupShowPending');
  const priorMissMfcRank = useUserSelector(id, 'priorMissMfcRank');
  const groupShowRoomCount = useUserSelector(id, 'groupShowRoomCount');
  const isBroadcasting = useUserSelector(id, 'isBroadcasting');
  const isTrendingNew = useUserSelector(id, 'isTrendingNew');
  const isRecommended = useUserSelector(id, 'isPinnedToTopModelYouMayLike');
  const isTruePrivate = useUserSelector(id, 'isTruePrivate');
  const isPrivateShow = useUserSelector(id, 'isPrivateShow');
  const trendingDiff = useUserSelector(id, 'trendingDiff');
  const missMFCPrizeFromSelector = useUserSelector(id, 'missMFCPrize');
  const missMFCPrize = missMFCPrizeOverride ?? missMFCPrizeFromSelector;
  const isPublicShow = useUserSelector(id, 'isPublicShow');
  const isAnonymous = isAnonymousMissMFC(id);
  const missMFCRankFromSelector = useUserSelector(id, 'missMFCRank');
  const missMFCRank = isAnonymous ? getAnonymousMissMFCRank(slug) : missMFCRankOverride ?? missMFCRankFromSelector;
  const isMissMFCFromSelector = useUserSelector(id, 'isMissMFC');
  const isMissMFC = isAnonymous || !!missMFCRankOverride || isMissMFCFromSelector;
  const isGroupShow = useUserSelector(id, 'isGroupShow');
  const isBookmark = useUserSelector(id, 'isBookmark');
  const isNewModel = useUserSelector(id, 'isNewModel');
  const isClubShow = useUserSelector(id, 'isClubShow');
  const isPopular = useUserSelector(id, 'isPopular');
  const isOffline = useUserSelector(id, 'isOffline');
  const isNGVideo = useUserSelector(id, 'isNGVideo');
  const isLounge = useUserSelector(id, 'isLounge');
  const isFriend = useUserSelector(id, 'isFriend');
  const isMember = useUserSelector(id, 'isMember');
  const isKicked = useUserSelector(id, 'isKicked');
  const creation = useUserSelector(id, 'creation');
  const viewers = useUserSelector(id, 'viewers');
  const channel = useUserSelector(id, 'channel');
  const isModel = useUserSelector(id, 'isModel');
  const hasNote = useUserSelector(id, 'hasNote');
  const server = useUserSelector(id, 'server');
  const isAway = useUserSelector(id, 'isAway');
  const phase = useUserSelector(id, 'phase');
  const color = useUserSelector(id, 'color');
  const availableWidth = columnWidth - gridPadding;
  const stylesName = getStyle('name');
  // const stylesBannerPX = getStyle('banner', { px: true })
  const stylesBanner = getStyle('banner');
  const stylesBannerPopular = stylesBanner('popular');
  const stylesBannerPopularViewers = stylesBannerPopular('viewers');
  const stylesBannerTrending = stylesBanner('trending');
  const stylesBannerTrendingDiff = stylesBannerTrending('diff');
  const stylesBannerNew = stylesBanner('new');
  const stylesBannerNewTime = stylesBannerNew('time');
  const stylesBannerMissMFC = stylesBanner('missMFC');
  const stylesBannerMissMFCRank = stylesBannerMissMFC('rank');
  const stylesUtilities = getStyle('utilities');
  const stylesBadge = getStyle('badge');
  const stylesBadgeStatus = stylesBadge('status');
  const stylesBadgeNewMissMFC = stylesBadge('newMissMFC');
  const stylesPriorMissMFCBadge = stylesBadge('priorMissMFC');
  const stylesColumnMaxWidth = getStyle('column')('maxWidth');
  const stylesViewers = getStyle('viewers');
  const isInfoOpen = useGridAvatarInfoOpenAppState(id);

  // const [isLargePreviewPinned, setLargePreviewPinned] = useState(false)
  const [canShowRoomPreview, setCanShowRoomPreview] = useState(false);
  const [settingUpRoomPreview, setupRoomPreview] = useState(isInfoOpen);
  const [showRoomPreview, setRoomPreview] = useState(false);
  const hasUserMousedOut = useRef(null);
  const momentumSettleUnsubRef = useRef(null);
  const animationRef = useRef(null);
  const animationRecoveryRef = useRef(null);
  const animationDebounceRef = useRef(null);
  const isRoomPinned = useIsRoomPinned(id);
  const isPreviewPinned = useFloatingPreviewSelector(id, 'close');
  const inHiddenShow = isTruePrivate || isPrivateShow || isGroupShow || isClubShow;
  const noShowType = isOffline || inHiddenShow || isAway;
  const isViewable = !noShowType && !isBanned && !isKicked;
  const showSinglePin = singlePin && isViewable;
  // Away, offline and hidden-show states render the poster, which stays 16:9.
  const isPreviewFrame43 = isSmallMobileTiles && isViewable && isBroadcasting;
  const onFriendCallback = useCallback(() => {
    const proceed = onFriend(id, isFriend);
    if (proceed === false) {
      return;
    }
    if (isFriend) {
      removeFriend(id, !isGuest);
    } else {
      addFriend(id, !isGuest);
    }
  }, [isGuest, isFriend, id]);
  const onBookmarkCallback = useCallback(() => {
    const proceed = onBookmark(id, isBookmark);
    if (proceed === false) {
      return;
    }
    if (isBookmark) {
      removeBookmark(id, !isGuest);
    } else {
      bookmarkModel(id, !isGuest);
    }
  }, [isGuest, isBookmark, id]);
  const onOpenInfoCallback = useCallback(() => {
    setupRoomPreview(true);
    setAvatarInfoOpenID(id);
  }, [id]);
  const onCloseInfoCallback = useCallback(() => {
    setCanShowRoomPreview(false);
    setupRoomPreview(false);
    setRoomPreview(false);
    if (isInfoOpen) {
      resetAvatarInfoOpenIDState();
    }
    // setUtilities(false)
  }, [isInfoOpen]);
  useEffect(() => {
    if (!willShowPreviewOnScroll) {
      return;
    }
    const timeout = setTimeout(() => {
      setupRoomPreview(true);
      setCanShowRoomPreview(true);
    }, PREVIEW_ON_SCROLL_DELAY);
    return () => {
      clearTimeout(timeout);
      setCanShowRoomPreview(false);
      setupRoomPreview(false);
      setRoomPreview(false);
    };
  }, [willShowPreviewOnScroll]);
  useEffect(() => {
    clearAvatarInfoOpenTimer(id);
    return () => {
      clearTimeout(animationDebounceRef.current);
      clearTimeout(animationRecoveryRef.current);
      momentumSettleUnsubRef.current?.();
      momentumSettleUnsubRef.current = null;
      if (isInfoOpen) {
        clearAvatarInfoOpenID(id);
      }
    };
  }, []);
  useEffect(() => {
    if (isInfoOpen) {
      setupRoomPreview(true);
      setCanShowRoomPreview(true);
    } else {
      setCanShowRoomPreview(false);
      setupRoomPreview(false);
      setRoomPreview(false);
    }
  }, [isInfoOpen]);
  const onOpenMenuCallback = useCallback(() => {
    onCloseInfoCallback();
    openMenuDialog(slug, {
      hideNextPrev: disableMenuNextPrev,
      transitionSource: TransitionTypes.TILE
    });
  }, [onCloseInfoCallback, slug, disableMenuNextPrev]);
  const onOpenContextMenuCallback = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isAnonymous || disableInteractions) return;
    onOpenMenuCallback();
    return false;
  }, [isAnonymous, disableInteractions, onOpenMenuCallback]);

  // const onLargeFloatingPinnedCallback = useCallback(
  //     (isPinned) => {
  //         if (isPinned) {
  //             setLargePreviewPinnedIDState(id)
  //             return
  //         }

  //         setLargePreviewPinnedIDState(null)

  //         // setLargePreviewPinned(isPinned)
  //     },
  //     [id],
  // )

  const onGoToRoomCallback = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    if (isAnonymous || disableInteractions) return;
    onCloseInfoCallback();
    if (onOpen) {
      onOpen(event);
    } else if (loungeSlug) {
      // if we're in the lounge, set the surrogate
      setLoungeSurrogate(id, loungeSlug);
    } else {
      openRoom(slug, {
        transitionSource: TransitionTypes.TILE
      });
    }
  }, [isAnonymous, disableInteractions, id, slug, loungeSlug, onOpen]);
  const onPinCallback = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    motionTransition(() => pinRoom(id, {
      pinned: !isRoomPinned
    }), {
      id,
      skipTransition: isRoomPinned,
      // skip if we're unpinning
      transitionSource: TransitionTypes.TILE,
      transitionDestination: TransitionTypes.PINROOM
    });
    // pinRoom(id, { pinned: !isRoomPinned })
    onCloseInfoCallback();
  }, [id, isRoomPinned]);
  const onOpenNote = useCallback(event => {
    event.stopPropagation();
    goToNotes(name);
  }, [id, name]);

  // if (!id) {
  //     return <AvatarSkeleton width={width} height={rowHeight} />
  // }

  const modelStatus = resolveModelStatus({
    isTruePrivate,
    isPrivateShow,
    isGroupShow,
    isClubShow,
    isAway
  });

  //
  // For testing purposes:
  //
  // const candisplayFriendBadge = true
  // const canDisplayBookmarkBadge = true
  // const canDisplayTrendingBadge = true
  // const canDisplayPopularBadge = true
  // const canDisplayNewModelBadge = true
  // const canDisplayMissMFCBadge = true
  // const canDisplayStatusBadge = true
  // const canDisplayPriorMissMFCBadge = true
  // const canDisplayRecentlyVisitedBadge = true
  // const canDisplayNotesButton = true
  // const canDisplayOnlineBadge = true
  // const canEnablePinToTopSymbols = false

  //
  // Animation
  //
  const fadeInPreview = settingUpRoomPreview && canShowRoomPreview && (showRoomPreview || !isPublicShow);
  // a public show holds the fade until its first snapshot or video frame
  // arrives; on a slow network that wait would otherwise show nothing but
  // the avatar. A banned room only renders a text poster, which never
  // reports a load, so it gets no spinner.
  const isPreviewLoading = settingUpRoomPreview && canShowRoomPreview && isPublicShow && !isBanned && !showRoomPreview;
  const previewLoadingSize = Math.round(Math.min(32, Math.max(16, displayHeight * (9 / 16) * 0.2)));

  //
  // Symbols
  //
  const canEnablePinToTopSymbols = enablePinToTopSymbols;

  //
  // Buttons
  //
  const canDisplayAllHeaderButtons = !isTouchDevice && availableWidth > 177;
  const canDisplayInfoButton = isTouchDevice && !disableInteractions;
  const canDisplayInfoCloseButton = canDisplayInfoButton;
  const canShowRoomButton = !isSmallMobileTiles;
  const canDisplayLargePreviewButton = enableLargePreview && isPublicShow && !canDisplayInfoButton;
  const canDisplayBookmarkButton = !isSmallMobileTiles && (canDisplayAllHeaderButtons || !canDisplayLargePreviewButton && !canDisplayInfoCloseButton);
  const canDisplayPinButton = (enablePin && !pinLimitReached || isRoomPinned || showSinglePin) && isPublicShow;
  const canDisplayFriendButton = !isSmallMobileTiles && (canDisplayAllHeaderButtons || !canDisplayPinButton);
  const canDisplayNotesButton = hasNote && displayNoteIconButton;
  const canDisplayNotesHeaderButton = canDisplayNotesButton && !isSmallMobileTiles;

  //
  // Banners
  //
  const canDisplayGroupShowBanner = displayGroupShowBanner && (isGroupShow || isGroupShowPending || groupShowRoomCount);
  const canDisplayPrivateShowBanner = displayPrivateShowBanner && (isPrivateShow || isTruePrivate);
  const canDisplayClubShowBanner = displayClubShowBanner && isClubShow;
  const canDisplayTrendingBanner = displayTrendingBanner && isTrendingNew && trendingDiff;
  const canDisplayNewModelBanner = displayNewModelBanner && isNewModel;
  const canDisplayPopularBanner = displayPopularBanner && isPopular && viewers;
  const canDisplayMissMFCBanner = displayMissMFCBanner && isMissMFC && missMFCRank;

  //
  // Badges
  //
  const candisplayFriendBadge = displayFriendBadge && isFriend;
  const canDisplayBookmarkBadge = displayBookmarkBadge && isBookmark;
  const canDisplayTrendingBadge = displayTrendingBadge && (!canEnablePinToTopSymbols || isPinnedToTopTrending) && isTrendingNew && trendingDiff;
  const canDisplayPopularBadge = displayPopularBadge && (!canEnablePinToTopSymbols || isPinnedToTopPopular) && isPopular && viewers;
  const canDisplayNewModelBadge = displayNewModelBadge && isNewModel;
  const canDisplayOnlineBadge = displayOnlineBadge && !settingUpRoomPreview;
  const canDisplayMissMFCBadge = displayMissMFCBadge && (!canEnablePinToTopSymbols || isPinnedToTopMissMFC || sort === 'miss_mfc') && isMissMFC && missMFCRank > 0;
  const canDisplayStatusBadge = displayStatusBadge && !!modelStatus && !settingUpRoomPreview;
  const canDisplayPriorMissMFCBadge = !!priorMissMfcRank;
  const canDisplayRecentlyVisitedBadge = displayRecentlyVisitedBadge && (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited);

  //
  // Viewers
  //
  const canShowViewers = !!viewers && !canDisplayPopularBanner;

  //
  // Highlights
  //
  const canDisplayActiveRoomHighlight = activeRoom === id;
  const canDisplayFriendHighlight = displayFriendHighlight && isFriend;
  const canDisplayBookmarkHighlight = displayBookmarkHighlight && isBookmark;
  const canDisplayPopularHighlight = displayPopularHighlight && isPopular && (!canEnablePinToTopSymbols || isPinnedToTopPopular);
  const canDisplayTrendingHighlight = displayTrendingHighlight && isTrendingNew && (!canEnablePinToTopSymbols || isPinnedToTopTrending);
  const canDisplayRecentlyViewedHighlight = displayRecentlyVisitedHighlight && (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited);
  const canDisplayRecommendationBadge = displayRecommendationBadge && (!canEnablePinToTopSymbols || isRecommended);
  const canDisplayRecommendationHighlight = displayRecommendationHighlight && isRecommended && (!canEnablePinToTopSymbols || isRecommended);
  const highlight = resolveDisplayHighlight({
    displayRecentlyVisitedHighlight: canDisplayRecentlyViewedHighlight,
    displayActiveRoomHighlight: canDisplayActiveRoomHighlight,
    displayTrendingHighlight: canDisplayTrendingHighlight,
    displayBookmarkHighlight: canDisplayBookmarkHighlight,
    displayPopularHighlight: canDisplayPopularHighlight,
    displayFriendHighlight: canDisplayFriendHighlight,
    displayRecommendationHighlight: canDisplayRecommendationHighlight
  });
  const canDisplayBanners = canDisplayGroupShowBanner || canDisplayPrivateShowBanner || canDisplayClubShowBanner || canDisplayTrendingBanner || canDisplayNewModelBanner || canDisplayMissMFCBanner || canDisplayPopularBanner;
  const canDisplayBadges = canDisplayBookmarkBadge || canDisplayTrendingBadge || canDisplayMissMFCBadge || canDisplayPopularBadge || canDisplayStatusBadge || candisplayFriendBadge || canDisplayRecommendationBadge;
  // Below 122px the ~76px clip badge cannot sit beside the menu button, and
  // a zoomed phone frame has these badges over its bottom-left: there the
  // clip badge keeps the SDK's top-left. A scroll preview hides them, so
  // it leaves the corner free
  const canPlaceClipBadgeBottom = availableWidth >= 122 && !(isPreviewFrame43 && canDisplayBadges && !willShowPreviewOnScroll);
  const availableUtilitySpace = (displayHeight - displayHeight * (9 / 16)) / 2;
  const badges = useMemo(() => [Number(canDisplayBookmarkBadge), Number(canDisplayTrendingBadge), Number(canDisplayPopularBadge), Number(candisplayFriendBadge)].reduce((memo, active) => memo + active, 0), [canDisplayTrendingBadge, canDisplayBookmarkBadge, canDisplayPopularBadge, candisplayFriendBadge]);

  // const isLargePreviewPinned =
  //     useGridLargePreviewPinnedAppState() === id &&
  //     canDisplayLargePreviewButton

  return <div data-testid="Container__ModelDisplay" key={id} id={`tile-${id}`} className={rootContainer}>
            <div data-testid="Container__Avatar" className={avatarContainer} style={{
      '--height': displayHeight + 'px'
    } as CSSProperties} onContextMenu={onOpenContextMenuCallback} onMouseOver={() => {
      if (isTouchDevice || disableInteractions) {
        return;
      }
      if (isMouseMovementHighState()) {
        // arm: open once the movement settles, if the
        // pointer is still over this tile — only this tile
        // subscribes, so a momentum flip notifies at most
        // one component
        hasUserMousedOut.current = false;
        momentumSettleUnsubRef.current ||= onHoverMomentumSettled(() => {
          momentumSettleUnsubRef.current?.();
          momentumSettleUnsubRef.current = null;
          if (hasUserMousedOut.current === false) {
            setupRoomPreview(true);
          }
        });
        return;
      }
      clearTimeout(animationDebounceRef.current);
      clearTimeout(animationRecoveryRef.current);
      hasUserMousedOut.current = false;
      if (!animationRef.current) {
        return;
      }
      animationDebounceRef.current = setTimeout(() => {
        setupRoomPreview(true);
      }, TIMEOUT_ANIMATION_AVATAR);
      setupRoomPreview(true);
    }} onMouseLeave={() => {
      if (isTouchDevice || disableInteractions) {
        return;
      }

      // if (!isLargePreviewPinned) {
      //     setLargePreview(id, null, false, false)
      // }

      clearTimeout(animationDebounceRef.current);
      clearTimeout(animationRecoveryRef.current);
      momentumSettleUnsubRef.current?.();
      momentumSettleUnsubRef.current = null;
      hasUserMousedOut.current = true;
      if (!animationRef.current) {
        return;
      }
      setCanShowRoomPreview(false);
      setRoomPreview(false);
      animationRecoveryRef.current = setTimeout(() => {
        // sometimes the "onTransitionEnd" event does not fire
        // after mousing in and out quickly, this will kick in
        // when that happens
        setupRoomPreview(false);
      }, TIMEOUT_ANIMATION_AVATAR /* * 1.5 */);
    }}>
                {/* <div className="image-gradient-glare" /> */}
                <div className={headerRootContainerStyles} style={{
        '--height': displayHeight + 'px'
      } as CSSProperties}>
                    {!isAnonymous && !disableInteractions && !willShowPreviewOnScroll && <div className={cx(stylesHeaderContainer, settingUpRoomPreview && stylesHeaderContainerVisibility)} style={{
          '--height': availableUtilitySpace + 'px',
          '--width': availableWidth + 'px'
        } as CSSProperties} data-testid="Container__AvatarRoomPreview">
                                <HeaderUtility showLargePreviewButton={canDisplayLargePreviewButton}
          // onLargeFloatingPinned={
          //     onLargeFloatingPinnedCallback
          // }
          showInfoCloseButton={canDisplayInfoCloseButton} showBookmarkButton={canDisplayBookmarkButton} showRoomButton={canShowRoomButton}
          // isLargePreviewPinned={isLargePreviewPinned}
          showFriendButton={canDisplayFriendButton} showNotesButton={canDisplayNotesHeaderButton} iconSize={stylesUtilities('iconSize')} showPinButton={canDisplayPinButton} isVideoOverlay={isPreviewFrame43} onCloseInfo={onCloseInfoCallback} onBookmark={onBookmarkCallback} onFriend={onFriendCallback} onRoom={onGoToRoomCallback} isRoomPinned={isRoomPinned} onPinRoom={onPinCallback} onOpenNote={onOpenNote} isBookmark={isBookmark} isFriend={isFriend} isMember={isMember} id={id} />
                            </div>}
                    <div className={cx(stylesPreviewFrame, highlight && stylesPreviewFrameBorder, previewScrollEnabled && stylesPreviewFrameScrollCursorRing,
        // the ring hands off to the video: it stays lit
        // while the tile is merely targeted (the target
        // tracks the cursor on every slow-scroll pass)
        // and fades out once the preview actually fades in
        isScrollCursorTile && !fadeInPreview && stylesPreviewFrameScrollCursor)} style={{
          ...(highlight ? {
            '--highlight': highlight
          } : {}),
          '--max-height': (settingUpRoomPreview && !isPreviewFrame43 ? displayHeight * (9 / 16) : displayHeight) + 'px',
          '--height': displayHeight + 'px',
          '--box-shadow': frameShadow ?? layers.one
        } as CSSProperties} onTransitionEnd={() => {
          if (hasUserMousedOut.current) {
            setupRoomPreview(false);
            return;
          }
          setCanShowRoomPreview(true);
        }} ref={animationRef}>
                        {(settingUpRoomPreview || isAnonymous) && <Fade duration={FADE_TRANSITION_AVATAR - 100} in={fadeInPreview} appear={false}>
                                <div onClick={onGoToRoomCallback} draggable onDragStart={dragStart} onDrag={dragMove} onDragEnd={dragEnd}>
                                    <VideoCover />
                                    <LiveSnapStream style={{
                '--border-radius': (fadeInPreview && highlight ? shape.borderRadiusSmallMedium : 0) + 'px'
              }} className={cx(stylesLiveSnapStream, isPreviewFrame43 && stylesLiveSnapStreamScale, canPlaceClipBadgeBottom && stylesLiveSnapStreamBadgeBottom)} onClick={onGoToRoomCallback} banned={isBanned} channel={channel} height={displayHeight * (9 / 16)} hideControls isAway={isAway} isBroadcasting={isBroadcasting} isClubShow={isClubShow} isGroupShow={isGroupShow} isLounge={isLounge} isModel={isModel} isNGVideo={isNGVideo} isOffline={isOffline} isPrivateShow={isPrivateShow} isTruePrivate={isTruePrivate}
              // force re-creation if slug changes
              key={`model-tile-snap-stream-${slug}`} id={id} phase={phase} server={server} videoID={VideoType.ModelTile} width={columnWidth} isAnonymous={isAnonymous} onPreviewLoaded={() => {
                if (hasUserMousedOut.current) {
                  return;
                }
                setRoomPreview(true);
              }} onVideoPlaying={() => {
                if (hasUserMousedOut.current) {
                  return;
                }
                setRoomPreview(true);
              }}
              // suppressVideo={willShowPreviewOnScroll}
              compact={stylesColumnMaxWidth < 150} noBackground videoMuted />
                                </div>
                            </Fade>}
                        <Fade duration={FADE_TRANSITION_AVATAR} in={!fadeInPreview} appear={false}>
                            <div onClick={onGoToRoomCallback} draggable onDragStart={dragStart} onDragEnd={dragEnd}>
                                <CellImage className={stylesCellImage} size={stylesColumnMaxWidth <= 150 ? 150 : 300} slug={slug} id={id} />
                            </div>
                        </Fade>
                        {isPreviewLoading && <div data-testid="Avatar__PreviewLoading" className={stylesPreviewLoading} aria-hidden>
                                <div className={stylesPreviewLoadingDisc}>
                                    <CircularProgress size={previewLoadingSize} />
                                </div>
                            </div>}
                        {(canShowViewers || canDisplayNewModelBadge || canDisplayOnlineBadge || canDisplayPriorMissMFCBadge && !canDisplayMissMFCBadge) && <div className={newMfcBadgeContainerStyles} style={{
            '--font-size': stylesBadgeStatus('fontSize') + 'px',
            ...resolveNewBadgePosition({
              canDisplayRecentlyVisitedBadge,
              canDisplayNotesButton,
              canDisplayInfoButton,
              stylesBadgeStatus,
              isRoomPinned
            })
          } as CSSProperties}>
                                {canDisplayOnlineBadge && <OnlineBadge fontSize={stylesBanner('fontSize')} isBroadcasting={isBroadcasting} className={stylesOnlineBadge} />}
                                {canDisplayNewModelBadge && <NewBadge className={cx(stylesNewBadge, resolveNewBadgeBorderRadius({
              canDisplayRecentlyVisitedBadge,
              canDisplayNotesButton,
              canDisplayInfoButton,
              isRoomPinned
            }))} />}
                                {canShowViewers && <div className={cx(stylesViewerContainer, showRoomPreview && isPreviewFrame43 && stylesPreviewFrame43ViewerContainer)}>
                                        <IconViewers title={locales.modelTile.viewers} size={stylesViewers('iconSize')} color="#FFF" styles={{
                '--margin-bottom': stylesViewers('offsetY') + 'px'
              } as CSSProperties} />
                                        <span style={{
                '--font-size': stylesViewers('fontSize') + 'px'
              } as CSSProperties} className={stylesViewerCounter}>
                                            {viewers}
                                        </span>
                                    </div>}
                                {canDisplayPriorMissMFCBadge && !canDisplayMissMFCBadge && !willShowPreviewOnScroll && <PriorMissMFCBadge className={cx(stylesPriorMissMFCBadge1, canDisplayNewModelBadge && stylesPriorMissMFCBadgeMargin)} style={{
              '--height': stylesPriorMissMFCBadge('altHeight') + 'px',
              '--width': stylesPriorMissMFCBadge('altWidth') + 'px'
            }}
            // stylesPriorMissMFCBadge(
            //     canDisplayNewModelBadge ?
            //     'margin' :
            //     'altMargin',
            // ),
            priorMissMfcRank={priorMissMfcRank} />}
                            </div>}
                        {(isRoomPinned || canDisplayNotesButton || canDisplayRecentlyVisitedBadge) && !settingUpRoomPreview && <div className={statusContainer}>
                                    {isRoomPinned && !isTouchDevice && <Container className={stylesContainer} shadow>
                                            <PinIcon size={stylesUtilities('avatarIconSize')} color="#fff" filled />
                                        </Container>}
                                    {isRoomPinned && isTouchDevice && <PinButton title={(isRoomPinned ? 'Unpin' : 'Pin') + ' Model'} size={stylesUtilities('iconSize')} filled={isRoomPinned} shadow onClick={onPinCallback} />}
                                    {canDisplayNotesButton && !isTouchDevice && <Container className={stylesContainer} shadow>
                                                <NotesIcon size={stylesUtilities('avatarIconSize')} color="#fff" filled />
                                            </Container>}
                                    {canDisplayNotesButton && isTouchDevice && <NotesButton className={stylesNotesButton} size={stylesUtilities('avatarIconSize')} filled title="Open Notes" onClick={onOpenNote} />}
                                    {canDisplayRecentlyVisitedBadge && <Container className={stylesContainer} tooltipPlacement="bottom" title="Recently Visited" shadow>
                                            <RoomIcon size={stylesUtilities('avatarIconSize')} color="#fff" filled />
                                        </Container>}
                                </div>}
                        {(canDisplayInfoButton || isPreviewPinned) && !settingUpRoomPreview && <div className={infoContainer}>
                                    {canDisplayInfoButton && <InfoButton className={cx(stylesInfoButton, settingUpRoomPreview && stylesInfoButtonVisibility)} size={stylesUtilities('avatarIconSize')} onClick={onOpenInfoCallback} />}
                                    {isPreviewPinned && !settingUpRoomPreview && <Container className={stylesContainerAlt}>
                                                <CameraIcon size={stylesUtilities('avatarIconSize')} color="#fff" filled />
                                            </Container>}
                                </div>}
                    </div>
                </div>
                {canDisplayBadges && <div className={badgeRootContainerStyles}>
                        <div className={badgeContainerStyles} style={{
          '--margin-bottom': (settingUpRoomPreview && (canDisplayStatusBadge || canDisplayMissMFCBadge) ? 0 : stylesBadgeStatus('offsetY')) + 'px',
          '--margin-left': stylesBadgeStatus('offsetX') + 'px'
        } as CSSProperties}>
                            {(candisplayFriendBadge || canDisplayBookmarkBadge || canDisplayTrendingBadge || canDisplayPopularBadge || canDisplayRecommendationBadge) && !willShowPreviewOnScroll && <div style={{
            '--justify-content': badges === 1 ? 'flex-start' : 'center'
          } as CSSProperties} className={stylesDisplayBadges}>
                                        {candisplayFriendBadge && <FriendBadge size={stylesBadgeStatus('iconSize')} />}
                                        {canDisplayBookmarkBadge && <BookmarkBadge size={stylesBadgeStatus('iconSize')} />}
                                        {canDisplayRecommendationBadge && <RecommendationBadge size={stylesBadgeStatus('iconSize')} />}
                                        {canDisplayPopularBadge && <PopularBadge size={stylesBadgeStatus('iconSize')} />}
                                        {canDisplayTrendingBadge && <TrendingBadge size={stylesBadgeStatus('iconSize')} />}
                                    </div>}
                            {canDisplayMissMFCBadge && !willShowPreviewOnScroll && <div className={stylesDisplayMissMFCBadge}>
                                        {canDisplayMissMFCBadge && <div className={missMfcBadgeStyles} style={{
              '--font-size': stylesBadgeStatus('fontSize') + 'px'
            } as CSSProperties}>
                                                <MissMFCBadge compact={stylesBadgeNewMissMFC('compact')} missMFCRank={missMFCRank} />
                                            </div>}
                                        {canDisplayPriorMissMFCBadge && <PriorMissMFCBadge className={stylesPriorMissMFCBadge2} style={{
              '--height': stylesPriorMissMFCBadge('height') + 'px',
              '--width': stylesPriorMissMFCBadge('width') + 'px'
            }} priorMissMfcRank={priorMissMfcRank} />}
                                    </div>}
                            {!!canDisplayStatusBadge && <StatusBadge className={cx(stylesStatusBadge, settingUpRoomPreview && stylesStatusBadgeRoomPreview)} style={{
            '--font-size': stylesBadgeStatus('fontSize') + 'px'
          }} status={modelStatus} shadow />}
                        </div>
                    </div>}
                <div className={modelMenuContainerStyles}>
                    <Grow in={fadeInPreview && (!hasUserMousedOut.current || isTouchDevice) && !isAnonymous && !disableInteractions}>
                        <div style={{
            // visibility:
            //     fadeInPreview &&
            //     (!hasUserMousedOut.current || isTouchDevice) &&
            //     !isAnonymous
            //         ? null
            //         : 'hidden',
            '--margin-bottom': getStyle('menu')('offsetY') + 'px'
          } as CSSProperties} className={cx(stylesModelMenuButtonContainer, showRoomPreview && isPreviewFrame43 && stylesPreviewFrame43MenuContainer)}>
                            <ButtonModelMenu onClick={onOpenMenuCallback} />
                        </div>
                    </Grow>
                </div>
            </div>
            {canDisplayBanners && <>
                    {canDisplayMissMFCBanner && <MissMFCBanner className={stylesHeightAndFontSize} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} rankClassName={cx(stylesMargin, ...(missMFCRankOverride && missMFCRank <= 3 ? [missMFCRank === 1 ? stylesMissMFCGold : stylesMissMFCSilver] : []))} rankStyle={{
        '--margin': stylesBannerMissMFCRank('margin') // TODO (Marc): Normalize this to exclude "px"
      }} text={missMFCRankOverride ? '' : stylesBannerMissMFC('text')} missMFCPrize={missMFCPrize} missMFCRank={missMFCRank} />}
                    {canDisplayTrendingBanner && <TrendingBanner className={stylesHeightAndFontSize} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} diffClassName={stylesMargin} diffStyle={{
        '--margin': stylesBannerTrendingDiff('margin') // TODO (Marc): Normalize this to exclude "px"
      }} hideIcon={availableWidth < stylesBanner('minimumWidth')} text={stylesBannerTrending('text')} diff={trendingDiff} />}
                    {canDisplayPopularBanner && <PopularBanner className={stylesHeightAndFontSize} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} viewersClassName={stylesMargin} viewersStyle={{
        '--margin': stylesBannerPopularViewers('margin') // TODO (Marc): Normalize this to exclude "px"
      }} hideIcon={availableWidth < stylesBanner('minimumWidth')} iconSize={stylesBanner('iconSize')} text={stylesBannerPopular('text')} viewers={viewers} />}

                    {canDisplayGroupShowBanner && (isGroupShow || isGroupShowPending || groupShowRoomCount) && <GroupShowBanner className={stylesHeightAndFontSize} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} iconSize={stylesBanner('iconSize')} roomCount={groupShowRoomCount} isLive={isGroupShow} />}
                    {canDisplayPrivateShowBanner && (isPrivateShow || isTruePrivate) && <PrivateShowBanner className={stylesHeightAndFontSize} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} iconSize={stylesBanner('iconSize')} isTruePrivate={isTruePrivate} />}
                    {canDisplayClubShowBanner && isClubShow && <ClubShowBanner className={stylesHeightAndFontSize} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} iconSize={stylesBanner('iconSize')} />}
                    {canDisplayNewModelBanner && <NewModelBanner className={stylesHeightAndFontSize} style={{
        '--font-size': stylesBanner('fontSize') + 'px',
        '--height': stylesBanner('height') + 'px'
      }} timeClassName={stylesMargin} timeStyle={{
        '--margin': stylesBannerNewTime('margin') // TODO (Marc): Normalize this to exclude "px"
      }} compact={columnWidth < 135} creation={creation} />}
                </>}
            <ModelName className={cx(stylesModelName, canDisplayBanners && stylesModelNameBorderTop, canDisplayBanners && stylesModelNameCenter)} style={{
      '--font-size': stylesName('fontSize')() + 'px',
      '--height': stylesName('height') + 'px',
      '--preview-margin-top': willShowPreviewOnScroll && !isPreviewFrame43 ? (columnCount === 2 ? -30 : columnCount === 3 ? -21 : -17) + 'px' : '0px'
    }} onClick={onGoToRoomCallback} isAnonymous={isAnonymous} color={color} modelId={id} name={name} />
        </div>;
});
export default Avatar;